/**
 * ExpressiveCameraCards — §3 "media-forward consumer drama" cameras renderer.
 *
 * Photo-hero grid: sm:grid-cols-2 xl:grid-cols-3 gap-5
 * Each card: rounded-2xl overflow-hidden relative h-56 group cursor-pointer
 * - Latest detection thumbnail as full-cover background (object-cover absolute
 *   inset-0 w-full h-full) — uses latest_detection_thumbnail directly (same
 *   field as the upstream CameraThumbnail). Hover: scale-105 + transition.
 * - Offline: grayscale brightness-50 on the img + a small status chip.
 * - Bottom glass info bar: name (font-display font-semibold), pills row.
 * - Actions menu floating top-right on glass (hidden in selection mode).
 * - Selection checkbox floats top-left in selection mode.
 *
 * data-testid="expressive-camera-card" on each card.
 */

import { useState } from 'react'
import {
  MoreVertical,
  Trash2,
  Settings,
  MapPin,
  Signal,
  Camera as CameraIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { OnlineStatusBadge } from '@/components/cameras/OnlineStatusBadge'
import { IngestionTypeBadge } from '@/components/cameras/IngestionTypeBadge'
import { cn } from '@/lib/utils'
import type { Camera } from '@/types'

// ── sub-components ─────────────────────────────────────────────────────────────

function CoverImage({
  src,
  alt,
  isOffline,
}: {
  src: string | null
  alt: string
  isOffline: boolean
}) {
  const [error, setError] = useState(false)

  if (!src || error) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-muted">
        <CameraIcon className="h-12 w-12 text-muted-foreground/20" />
      </div>
    )
  }

  return (
    <img
      src={src}
      alt={alt}
      className={cn(
        'absolute inset-0 object-cover w-full h-full transition-transform duration-500 group-hover:scale-105',
        isOffline && 'grayscale brightness-50',
      )}
      onError={() => setError(true)}
    />
  )
}

function SelectCheckbox({ selected }: { selected: boolean }) {
  return (
    <div
      className={cn(
        'absolute top-2 left-2 z-20 h-5 w-5 rounded-full flex items-center justify-center',
        'bg-black/50 backdrop-blur-sm border-2 transition-colors',
        selected ? 'bg-primary border-primary' : 'border-white/70',
      )}
    >
      {selected && (
        <svg viewBox="0 0 8 8" className="h-2.5 w-2.5 fill-white">
          <path d="M1 4l2 2 4-4" stroke="white" strokeWidth="1.5" fill="none" />
        </svg>
      )}
    </div>
  )
}

// ── card ──────────────────────────────────────────────────────────────────────

interface ExpressiveCardProps {
  camera: Camera
  selected: boolean
  selectionMode: boolean
  onToggleSelect: (id: number) => void
  onDeleteRequest: (camera: Camera) => void
  onEditRequest: (camera: Camera) => void
  onMapRequest: (camera: Camera) => void
  onSimRequest: (camera: Camera) => void
  simUsageMB?: number | null
}

function ExpressiveCard({
  camera,
  selected,
  selectionMode,
  onToggleSelect,
  onDeleteRequest,
  onEditRequest,
  onMapRequest,
  onSimRequest,
  simUsageMB,
}: ExpressiveCardProps) {
  const navigate = useNavigate()
  const { t } = useTranslation('cameras')

  // Camera is "offline" when is_online === false (HTTP cameras report status).
  // FTP cameras (is_online === null) are not greyed — they never report status.
  const isOffline = camera.is_online === false

  const handleClick = () => {
    if (selectionMode) { onToggleSelect(camera.id); return }
    if (camera.is_locked) {
      toast.error('Camera locked', { description: 'SIM mismatch — under review. Please contact support.' })
      return
    }
    navigate(`/events?camera=${camera.id}`)
  }

  return (
    <div
      data-testid="expressive-camera-card"
      className={cn(
        'rounded-2xl overflow-hidden relative h-56 group cursor-pointer',
        selected && 'ring-2 ring-primary ring-offset-1',
      )}
      onClick={handleClick}
    >
      {/* Full-cover thumbnail */}
      <CoverImage
        src={camera.latest_detection_thumbnail}
        alt={camera.name}
        isOffline={isOffline}
      />

      {/* Offline status chip overlay — positioned top-2 right-12 to clear
          the actions menu (top-right) and selection checkbox (top-left) */}
      {isOffline && !selectionMode && (
        <div className="absolute top-2 right-12 z-10 rounded-full bg-black/60 backdrop-blur-sm px-2 py-0.5 text-[11px] text-white/80">
          {t('status.offline')}
        </div>
      )}

      {/* Selection checkbox — top-left glass circle in selection mode */}
      {selectionMode && (
        <div onClick={(e) => { e.stopPropagation(); onToggleSelect(camera.id) }}>
          <SelectCheckbox selected={selected} />
        </div>
      )}

      {/* Actions menu — top-right, floating on glass, hidden in selection mode */}
      {!selectionMode && (
        <div
          className="absolute top-2 right-2 z-20"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 rounded-full border border-white/20"
              >
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEditRequest(camera)}>
                <Settings className="mr-2 h-4 w-4" />
                {t('card.cameraSettings')}
              </DropdownMenuItem>
              {camera.latitude && camera.longitude && (
                <DropdownMenuItem onClick={() => onMapRequest(camera)}>
                  <MapPin className="mr-2 h-4 w-4" />
                  {t('card.viewOnMap')}
                </DropdownMenuItem>
              )}
              {camera.iccid && (
                <DropdownMenuItem onClick={() => onSimRequest(camera)}>
                  <Signal className="mr-2 h-4 w-4" />
                  {t('card.simDetails')}
                  {simUsageMB != null && (
                    <span className="ml-1 text-[10px] text-muted-foreground">
                      {simUsageMB >= 1024
                        ? `${(simUsageMB / 1024).toFixed(1)} GB`
                        : `${Math.round(simUsageMB)} MB`}
                    </span>
                  )}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="text-destructive focus:text-destructive focus:bg-destructive/10"
                onClick={() => onDeleteRequest(camera)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('card.deleteCamera')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Bottom glass info bar */}
      <div className="bg-glass absolute inset-x-2 bottom-2 rounded-xl p-2.5 z-10 backdrop-blur-sm border border-white/10">
        <p className="font-display font-semibold text-[13px] leading-tight truncate text-foreground">
          {camera.name}
        </p>
        {/* Pills row */}
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          <IngestionTypeBadge type={camera.ingestion_type} />
          <OnlineStatusBadge isOnline={camera.is_online} lastSeen={camera.last_seen} />
        </div>
      </div>
    </div>
  )
}

// ── main component ─────────────────────────────────────────────────────────────

export interface ExpressiveCameraCardsHandlers {
  onToggleSelect: (id: number) => void
  onDeleteRequest: (camera: Camera) => void
  onEditRequest: (camera: Camera) => void
  onMapRequest: (camera: Camera) => void
  onSimRequest: (camera: Camera) => void
}

export interface ExpressiveCameraCardsProps extends ExpressiveCameraCardsHandlers {
  cameras: Camera[]
  selectionMode: boolean
  selectedIds: Set<number>
  simUsageMap: Record<string, { monthUsedMB: number }>
}

export function ExpressiveCameraCards({
  cameras,
  selectionMode,
  selectedIds,
  simUsageMap,
  onToggleSelect,
  onDeleteRequest,
  onEditRequest,
  onMapRequest,
  onSimRequest,
}: ExpressiveCameraCardsProps) {
  return (
    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
      {cameras.map((camera) => (
        <ExpressiveCard
          key={camera.id}
          camera={camera}
          selected={selectedIds.has(camera.id)}
          selectionMode={selectionMode}
          onToggleSelect={onToggleSelect}
          onDeleteRequest={onDeleteRequest}
          onEditRequest={onEditRequest}
          onMapRequest={onMapRequest}
          onSimRequest={onSimRequest}
          simUsageMB={
            camera.iccid && simUsageMap[camera.iccid]
              ? simUsageMap[camera.iccid].monthUsedMB
              : null
          }
        />
      ))}
    </div>
  )
}
